import { useLayoutEffect, useMemo } from "react";
import { useWatch } from "react-hook-form";
import useSpesificCustoms from "@/features/customField/api/useSpesificCustoms";
import useResolveCustomDefaults from "@/features/customField/api/useResolveCustomDefaults";
import { buildAlignedScopes, scopesEquivalent } from "./alignScopes";
import PrefrenceSelectors from "@/components/ui/others/PrefrenceRenderers/PrefrenceSelectors";
import { isTagSelector } from "@/features/customField/utils/cfSelectionMode";
import { CustomInputTypeEnums } from "@/features/customField/types/customInputTypes";
import TagsMultySelect from "../TagsMultySelect";
import GridContainer from "@/features/stage/components/add-edit-stage/confComponents/GridContainer";
import { bridgeCountryByValue } from "@/features/customField/utils/countryLock";

export default function PrefrenceFormRenderer({
  hook,
  queryKey,
  modelName,
  removeKeys,
  /** e.g. `"intake"` — skip fields whose name starts with this (case-insensitive). Used when the same custom field is rendered elsewhere (multi-select in another card). */
  hideIfNameStartsWith,
  col = 2,
  moduleScopes,
  /**
   * Country value(s) of the picked course's university (e.g. `["Uk"]`,
   * from `getCourseCountries`). Locks the deal's single-select Country field
   * to the matching option even when the server resolver returns nothing for
   * it (separate Country fields on university vs deal) — see `countryLock.ts`.
   */
  countryValues,
  /** Every picker spans the whole row (Add Application: single-select fields
   *  sat at half width beside full-width tag pickers). */
  fullWidth = false,
}: any) {
  const { errors, control, getValues, setValue } = hook();
  const { spesificFields } = useSpesificCustoms({
    queryKey,
    modelName,
    type: "SELECTOR",
  });

  const { resolvedFields: serverResolvedFields } = useResolveCustomDefaults(
    moduleScopes ?? [],
    modelName
  );

  // Stable key so the memo below only recomputes when the country set really
  // changes — `resolvedFields` identity feeds the align effect, and a fresh
  // array on every render is the setValue → re-render loop (React #185) the
  // resolver hook already guards against.
  const countryKey = Array.isArray(countryValues)
    ? countryValues
        .map((v: unknown) => String(v ?? "").trim().toLowerCase())
        .filter(Boolean)
        .sort()
        .join("|")
    : "";
  const resolvedFields = useMemo(
    () =>
      bridgeCountryByValue(
        serverResolvedFields,
        spesificFields,
        countryKey ? countryKey.split("|") : []
      ),
    [serverResolvedFields, spesificFields, countryKey]
  );

  const resolvedMap = useMemo(
    () => new Map(resolvedFields.map((rf) => [rf.customInputId, rf])),
    [resolvedFields]
  );

  // Every selector in this renderer — and the sibling pickers that share the
  // array, e.g. the course form's Intake Month multi-select — binds
  // `customInputScopes.<index>` by the field's POSITION in `spesificFields`.
  // The array must therefore stay aligned to that order at all times. It
  // used to be aligned only when the field list changed, which is BEFORE an
  // edit form's `reset(record)` lands the saved scopes in SERVER order: the
  // Intake Month picker then read another field's slot, its picks overwrote
  // that field, and the intake's own row (still holding the old picks) rode
  // along to the server — "I added the intake and it did not update"
  // (2026-09-08, Edit Course). Watching the array and re-aligning whenever it
  // drifts fixes every consumer at once; the equivalence guard is what makes
  // it loop-free (the resolver hook's React #185 history is exactly a
  // watcher + unconditional setValue).
  const watchedScopes = useWatch({ control, name: "customInputScopes" });

  useLayoutEffect(() => {
    if (
      !spesificFields?.length ||
      typeof getValues !== "function" ||
      typeof setValue !== "function"
    )
      return;
    const currentScopes = getValues("customInputScopes") ?? [];
    const aligned = buildAlignedScopes(
      spesificFields,
      currentScopes,
      resolvedFields
    );
    if (!scopesEquivalent(currentScopes, aligned)) {
      setValue("customInputScopes", aligned);
    }
  }, [spesificFields, resolvedFields, watchedScopes, getValues, setValue]);

  if (!spesificFields?.length) return null;

  return (
    <GridContainer columns={col} className="grid grid-cols-2 gap-4 w-full">
      {spesificFields?.map((val: any, index: number) => {
        if (val.type !== CustomInputTypeEnums.SELECTOR) return null;
        const nameLower = val.name.toLowerCase();
        if (removeKeys && removeKeys?.includes(nameLower)) return null;
        if (
          typeof hideIfNameStartsWith === "string" &&
          hideIfNameStartsWith.length > 0 &&
          nameLower.startsWith(hideIfNameStartsWith.toLowerCase())
        ) {
          return null;
        }

        const resolved = resolvedMap.get(val.id);

        // A LOCKED field renders as the read-only box regardless of its
        // Tag/Normal setting. `TagsMultySelect` has no lock support at all, so
        // a Tag Country field routed there stayed empty and editable next to
        // the course that already determines it (Skymark, 2026-09-09).
        // `PrefrenceSelectors` handles both the locked box and multi-select,
        // so the lock is the thing that picks the renderer here.
        const locked =
          Boolean(resolved?.autoFill) &&
          (resolved?.resolvedOptions?.length ?? 0) === 1;

        // Tag fields render the badge picker, Normal ones a single select —
        // both read the FIELD's own Tag/Normal setting, never its name.
        if (isTagSelector(val) && !locked) {
          return (
            <TagsMultySelect
              key={val.id}
              val={val}
              index={index}
              errors={errors}
              control={control}
            />
          );
        } else {
          return (
            <PrefrenceSelectors
              key={val.id}
              val={val}
              index={index}
              errors={errors}
              control={control}
              resolvedOptions={resolved?.resolvedOptions}
              autoFill={resolved?.autoFill}
              fullWidth={fullWidth}
            />
          );
        }
      })}
    </GridContainer>
  );
}
